import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { BarChart3, Users, Building, Receipt, AlertTriangle, Calendar, TrendingUp, Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, [token]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div></div>;
  }

  const statCards = [
    { label: 'Utilisateurs', value: stats?.total_users || 0, icon: Users, color: 'bg-blue-500' },
    { label: 'Gestionnaires', value: stats?.total_gestionnaires || 0, icon: Users, color: 'bg-purple-500' },
    { label: 'Copropriétaires', value: stats?.total_coproprietaires || 0, icon: Users, color: 'bg-green-500' },
    { label: 'Immeubles', value: stats?.total_buildings || 0, icon: Building, color: 'bg-orange-500' },
    { label: 'Cabinets', value: stats?.total_cabinets || 0, icon: Building, color: 'bg-cyan-500' },
    { label: 'Factures', value: stats?.total_invoices || 0, icon: Receipt, color: 'bg-yellow-500' },
    { label: 'Incidents', value: stats?.total_incidents || 0, icon: AlertTriangle, color: 'bg-red-500' },
    { label: 'Assemblées', value: stats?.total_assemblies || 0, icon: Calendar, color: 'bg-indigo-500' }
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard Admin</h1>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <TrendingUp size={16} />
          <span>Mis à jour en temps réel</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-4 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                <stat.icon size={24} className="text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Actions rapides</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <a href="/admin/cabinets" className="p-4 border border-gray-200 rounded-lg hover:border-[#00D3A8] hover:bg-gray-50 transition-all text-center">
            <Building size={24} className="mx-auto mb-2 text-[#00D3A8]" />
            <span className="text-sm font-medium">Gérer Cabinets</span>
          </a>
          <a href="/admin/produits" className="p-4 border border-gray-200 rounded-lg hover:border-[#00D3A8] hover:bg-gray-50 transition-all text-center">
            <Package size={24} className="mx-auto mb-2 text-[#00D3A8]" />
            <span className="text-sm font-medium">Gérer Produits</span>
          </a>
          <a href="/admin/commandes" className="p-4 border border-gray-200 rounded-lg hover:border-[#00D3A8] hover:bg-gray-50 transition-all text-center">
            <Receipt size={24} className="mx-auto mb-2 text-[#00D3A8]" />
            <span className="text-sm font-medium">Voir Commandes</span>
          </a>
          <a href="/admin/utilisateurs" className="p-4 border border-gray-200 rounded-lg hover:border-[#00D3A8] hover:bg-gray-50 transition-all text-center">
            <Users size={24} className="mx-auto mb-2 text-[#00D3A8]" />
            <span className="text-sm font-medium">Gérer Utilisateurs</span>
          </a>
        </div>
      </div>

      {/* Charts placeholder */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <BarChart3 size={20} className="mr-2 text-[#00D3A8]" />
            Activité récente
          </h2>
          <div className="h-48 flex items-center justify-center text-gray-400">
            <p>Graphique d'activité</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <TrendingUp size={20} className="mr-2 text-[#00D3A8]" />
            Revenus
          </h2>
          <div className="h-48 flex items-center justify-center text-gray-400">
            <p>Graphique de revenus</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
